import { Scene } from "../../core/Scene";
import { GlobalCluster } from "../../cluster/GlobalCluster";
import TextButton from "../../core/TextButton";
import { BrowserRequest } from "../../utils/BrowserRequest";
import { ScenesManager } from "../../core/SceneManager";
import { App } from "../../app";
import { ResultRowContainer } from "../../containers/ResultRowContainer";
import { Utils, BasicEvent } from "@h4x/common";
import Text from "../../core/Text";
import { Colors } from "../../utils/Colors";
import { ClusterCentroid } from "../../cluster/ClusterCentroid";
import { EnvironmentContainer } from "../../containers/EnvironmentContainer";
import { ClusterUtils } from "../../cluster/ClusterUtils";

type SingleRowFn = (row: number, centroids: ClusterCentroid[]) => void;

const MAX_STEPS = 1;

export class SynapseClusterTest extends Scene {
	constructor() {
		super();
	}

	private progressText = new Text("LOADING", { fill: Colors.White });
	private opsText = new Text("LOADING", { fill: Colors.White });
	private totalStepsText = new Text("LOADING", { fill: Colors.White });

	private rows: ResultRowContainer[] = [];
	private cluster?: GlobalCluster;

	private problemUUID: string;
	private loops: number = 1;

	private totalSteps: number = 0;

	private async initDataset() {
		this.cluster = undefined;

		let data = await ClusterUtils.loadData();
		this.problemUUID = data.problemUUID;
		this.cluster = new GlobalCluster(data.data);

		this.updateCentroids(undefined);
	}

	protected onActivated() {
		ScenesManager.setFullscreen(true);
	}

	protected onDeactivated() {
		ScenesManager.setFullscreen(false);
	}

	private updateCentroids(centroids?: { type: boolean; values: number[]; }[]) {
		this.progressText.text = "LOADING";
		this.opsText.text = "LOADING";
		this.totalStepsText.text = "LOADING";

		this.lastBestScore = 0;
		this.lastBestTime = Date.now();
		this.totalSteps = 0;

		if (this.cluster === undefined) {
			throw new Error("Missing cluster!");
		}

		ClusterUtils.updateClusterCentroids(this.cluster, centroids, 1, 1);

		this.cluster.calculate();
		this.temp = undefined!;
		this.tempValid = false;

		for (const row of this.rows) {
			this.uiContainer.removeChild(row);
		}
		this.rows = [];

		for (let i = 0; i < this.cluster.count; i++) {
			let rowContainer = new ResultRowContainer(this.cluster, i);
			rowContainer.x = 109;
			rowContainer.y = 50 + i * 10;
			this.uiContainer.addChild(rowContainer);
			this.rows.push(rowContainer);
		}
	}

	private lastBestScore = 0;
	private lastBestTime = Date.now();
	private resultsCount = 0;

	public async init() {
		let environment = new EnvironmentContainer(Colors.DarkGray);
		this.backgroundContainer.addChild(environment);

		{
			let button = new TextButton("Back").setPosition(5, 5);
			button.onClick.addCallback(() => { ScenesManager.changeScene(App.debugMenuScene); });
			this.uiContainer.addChild(button);
		}

		{
			let button = new TextButton("Restart").setPosition(100, 5);
			button.onClick.addCallback(() => { this.restart(); });
			this.uiContainer.addChild(button);
		}

		let updateEvent = new BasicEvent();

		this.uiContainer.addChild(new Text("Loops", { fill: Colors.LightGray, fontSize: 13 }).setPosition(5, 450));

		{
			let i = 0;
			for (const v of [1, 5, 10, 25, 50, 100, 200, 500, 1000]) {
				let button = new TextButton("" + v, { fontSize: 13 }).setPosition(10, 470 + 20 * i++);
				button.onClick.addCallback(() => { this.loops = v; updateEvent.execute(); });
				updateEvent.addCallback(() => { button.setMainColor((v === this.loops) ? 0x00CC00 : 0xFFFFFF); });
				this.uiContainer.addChild(button);
			}
		}
		updateEvent.execute();

		this.progressText.x = 300;
		this.progressText.y = 5;
		this.uiContainer.addChild(this.progressText);

		this.opsText.x = 500;
		this.opsText.y = 5;
		this.uiContainer.addChild(this.opsText);

		this.totalStepsText.x = 650;
		this.totalStepsText.y = 5;
		this.uiContainer.addChild(this.totalStepsText);

		await this.initDataset();

		let ops = 0;
		setInterval(() => {
			if (this.active && this.cluster) {
				if (this.lastBestTime + 60 * 1000 < Date.now() || this.totalSteps >= MAX_STEPS) {
					console.log(this.lastBestScore, JSON.parse(JSON.stringify(this.cluster.getCentroids())));

					if (this.lastBestScore > 0.5) {
						BrowserRequest.post<{
							result: {
								balancedMode: boolean;
								problemUUID: string;
								data: { type: boolean; values: number[]; }[];
								centroids: { type: boolean; values: number[]; }[];
							};
						}>(ClusterUtils.ResultAPI, {
							body: {
								result: {
									problemUUID: this.problemUUID,
									data: {
										balancedMode: this.cluster.balancedMode,
										result: this.lastBestScore,
										centroids: this.cluster.getCentroids().map(x => { return { type: x.type, values: x.values }; })
									}
								},
								params: {
									score: this.lastBestScore
								}
							}
						});
					}

					// FIXME display the centroid counts and dataset
					let scoreText = new Text((this.lastBestScore * 100).toFixed(3) + "%", { fill: Colors.White, fontSize: 13 });
					scoreText.setPosition(930 + ((this.resultsCount / 50) | 0) * 50, 60 + ((this.resultsCount % 50) | 0) * 15);
					this.uiContainer.addChild(scoreText);
					this.resultsCount++;

					this.restart();
					return;
				}
				for (let loop = 0; loop < this.loops; loop++) {
					ops++;
					this.totalSteps++;
					if (this.totalSteps > MAX_STEPS) {
						break;
					}

					let globalScore = this.optimizeStep(this.totalSteps);
					if (this.lastBestScore < globalScore) {
						this.lastBestScore = globalScore;
						this.lastBestTime = Date.now();
					}
				}

				this.progressText.text = (this.cluster.globalScore * 100).toFixed(4) + "%";
				this.rows.forEach(x => x.update());

				// text.text = (cluster.globalScore * 100).toFixed(4) + "%";

				// pyramidContainer.update();
				// solveContainer.update();
				// rows.forEach(x => x.update());
			}
		}, 1 * 1);

		setInterval(() => {
			if (this.active && this.cluster) {
				this.opsText.text = ops + " op/s";
				this.totalStepsText.text = "Total steps: " + this.totalSteps;
				ops = 0;
			}
		}, 1 * 1000);


		// this.uiContainer.addChild(pyramidContainer);
		// this.uiContainer.addChild(solveContainer);
	}

	private static rowOptimizers = [
		(row: number, centroids: ClusterCentroid[]) => { // randomize all centroids
			for (const centroid of centroids) { centroid.values[row] = Math.random(); }
		},
		(row: number, centroids: ClusterCentroid[]) => { // randomize single centroid
			Utils.pickArray(centroids)!.values[row] = Math.random();
		},
		(row: number, centroids: ClusterCentroid[]) => { // shift all centroids
			let maxDist = 0.01 + Math.random() * 0.2;
			for (const centroid of centroids) {
				let value = centroid.values[row] + (Math.random() * maxDist);
				value = Math.min(Math.max(0, value), 1);
				centroid.values[row] = value;
			}
		}
	];

	private static rowOptimizersLarge = [ // 4+
		...SynapseClusterTest.rowOptimizers,
		(row: number, centroids: ClusterCentroid[]) => { // shift multiple centroids
			centroids = [...centroids].sort(() => .5 - Math.random());
			let maxDist = 0.01 + Math.random() * 0.2;
			for (const centroid of centroids.slice(0, Utils.rangeInt(2, centroids.length - 1))) {
				let value = centroid.values[row] + (Math.random() * maxDist);
				value = Math.min(Math.max(0, value), 1);
				centroid.values[row] = value;
			}
		}
	];

	private temp: GlobalCluster;
	private tempValid = false;
	private optimizeStep(step: number) {
		if (this.cluster === undefined) { throw new Error("Missing cluster!"); }

		if (this.tempValid === false) {
			this.temp = this.cluster.copy(this.temp);
		}
		this.tempValid = true;
		let centroids = this.temp.getCentroids();

		if (step % 2 === 1) {
			SynapseClusterTest.optimizeSingleRow(this.temp, centroids,
				Utils.pickArray((centroids.length >= 4) ? SynapseClusterTest.rowOptimizersLarge : SynapseClusterTest.rowOptimizers)!
			);
		} else {
			SynapseClusterTest.optimizeDoubleRow(this.temp, centroids,
				Utils.pickArray((centroids.length >= 4) ? SynapseClusterTest.rowOptimizersLarge : SynapseClusterTest.rowOptimizers)!,
				Utils.pickArray((centroids.length >= 4) ? SynapseClusterTest.rowOptimizersLarge : SynapseClusterTest.rowOptimizers)!
			);
		}

		let tempScore = this.temp.globalScore;
		let currentScore = this.cluster.globalScore;
		if (tempScore > currentScore) {
			this.temp.copy(this.cluster);
			return tempScore;
		} else if (tempScore !== currentScore) {
			this.tempValid = false;
		}
		return currentScore;
	}


	private static optimizeSingleRow(cluster: GlobalCluster, centroids: ClusterCentroid[], fn: SingleRowFn) {
		let row: number; // = Utils.rangeInt(0, cluster.count);
		if (Utils.rangeFloat(0, 1) > 0.5) {
			row = Utils.randomWeightedArray(cluster.scoresY.map((x) => { return { weight: Math.pow(1 - x, 5) }; }))!.key;
		} else {
			row = Utils.rangeInt(0, cluster.count);
		}

		fn(row, centroids);
		cluster.calculate(row);
	}

	private static optimizeDoubleRow(cluster: GlobalCluster, centroids: ClusterCentroid[], fn1: SingleRowFn, fn2: SingleRowFn) {
		let row1: number; // = Utils.rangeInt(0, cluster.count);
		if (Utils.rangeFloat(0, 1) > 0.5) {
			row1 = Utils.randomWeightedArray(cluster.scoresY.map((x) => { return { weight: Math.pow(1 - x, 5) }; }))!.key;
		} else {
			row1 = Utils.rangeInt(0, cluster.count);
		}

		let row2: number; // = Utils.rangeInt(0, cluster.count);
		if (Utils.rangeFloat(0, 1) > 0.5) {
			row2 = Utils.randomWeightedArray(cluster.scoresY.map((x) => { return { weight: Math.pow(1 - x, 5) }; }))!.key;
		} else {
			row2 = Utils.rangeInt(0, cluster.count);
		}

		fn1(row1, centroids);
		cluster.calculate(row1);
		fn2(row2, centroids);
		cluster.calculate(row2);
	}

	private async restart() {
		(this as any).active = false;
		if (this.cluster === undefined) { throw new Error("Missing cluster!"); }
		this.totalSteps = 0;
		this.lastBestScore = 0;
		this.lastBestTime = Date.now();

		let data = await ClusterUtils.loadData();
		this.problemUUID = data.problemUUID;
		let temp = new GlobalCluster(data.data);
		temp.copy(this.cluster);
		this.updateCentroids(data.centroids);

		this.temp = undefined!;
		this.tempValid = false;
		(this as any).active = true;
	}

}
